import { useCallback, useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import { getItem, getList } from "../../api/generics";
import { getRoutePath } from "../../routes";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CCollapse,
  CDataTable,
  CInput,
  CInputGroup,
  CInputGroupText,
  CLabel,
  CPagination,
  CRow,
  CTooltip,
} from "@coreui/react";

import moment from "moment";

import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";

import { BsFillPlusCircleFill } from "react-icons/bs";
import ReceivedInvoiceDeleteModal from "./ReceivedInvoiceDeleteModal";
import { formatToCurrency } from "../../currency/format";

import { formatUTCDateTime } from "../../utils/dates";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { getObjectMinioUrl } from "../../minio/urls";

import ReceivedInvoiceCrudActions from "./ReceivedInvoiceCrudActions";

import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import { INVOICES_CREATE, INVOICES_RETRIEVE } from "../../auth/permissions";

import { emptyValueOnUndefined } from "../../utils/fields";

import { useIsMobile } from "../../hooks/is-mobile";
import { ReceivedInvoiceCard } from "./ReceivedInvoiceMobileCard";
import {
  formatReceivedInvoiceNumber,
  ReceivedInvoice,
} from "../../models/received-invoice";
import SystemNotificationBanners from "../notifications/SystemNotificationBanners";

const ITEMS_PER_PAGE = 20;

const ReceivedInvoiceCrud = () => {
  const fields = [
    { key: "invoiceDate", _classes: ["text-center"], label: "Fecha" },
    { key: "invoiceNumber", _classes: ["text-center"], label: "Nro. Factura" },
    { key: "supplierRuc", _classes: ["text-center"], label: "RUC" },
    {
      key: "supplierSocialName",
      _classes: ["text-center"],
      label: "Razón Social",
    },
    { key: "amount", _classes: ["text-center"], label: "Monto" },
    { key: "files", _classes: ["text-center"], label: "Archivos" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  // Filters
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<ReceivedInvoice[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // Delete control
  const [showInvoiceDeleteModal, setShowInvoiceDeleteModal] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState<
    ReceivedInvoice | undefined
  >(undefined);

  // Filter Collapse control
  const [filterCollapse, setFilterCollapse] = useState(false);
  const toggleFilterCollapse = (e: React.ChangeEvent<HTMLButtonElement>) => {
    setFilterCollapse(!filterCollapse);
    e.preventDefault();
  };

  const history = useHistory();
  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }

    let search = urlParams.get("search") ? urlParams.get("search") : "";

    let startDateStr = urlParams.get("start_date")
      ? urlParams.get("start_date")
      : null;

    let startDate: Date | null = null;
    if (startDateStr !== null && startDateStr !== "") {
      startDate = new Date(startDateStr);
    }

    let endDateStr = urlParams.get("end_date")
      ? urlParams.get("end_date")
      : null;

    let endDate: Date | null = null;
    if (endDateStr !== null && endDateStr !== "") {
      endDate = new Date(endDateStr);
    }

    return {
      page: page,
      search: search,
      startDate: startDate,
      endDate: endDate,
    };
  };

  const fetchInvoices = useCallback(async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.page - 1);
    const additionalParams = new Map<string, string | string[]>();
    additionalParams.set("tax_payer_id", taxPayer.id!.toString());

    if (urlParams.search !== null && urlParams.search !== "") {
      additionalParams.set("search", urlParams.search);
    }

    if (urlParams.startDate !== null) {
      additionalParams.set("start_date", urlParams.startDate.toISOString());
    }

    if (urlParams.endDate !== null) {
      additionalParams.set("end_date", urlParams.endDate.toISOString());
    }

    setSearch(urlParams.search ? urlParams.search : "");
    setStartDate(urlParams.startDate);
    setEndDate(urlParams.endDate);

    const invoicesState = await getList<ReceivedInvoice>(
      "/received_invoices/",
      limit,
      offset,
      additionalParams,
    );

    if (invoicesState.status === SUCCESS) {
      if (invoicesState.data !== undefined) {
        const count = invoicesState.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setInvoices(invoicesState.data.items);
        setCurrentPage(urlParams.page);
      }
    } else {
      const message = invoicesState.detail
        ? invoicesState.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    history.push(getRoutePath("addReceivedInvoice"));
  };

  const onReadClick = (invoice: ReceivedInvoice) => {
    const baseUrl = getRoutePath("readReceivedInvoice");
    history.push(`${baseUrl}?id=${invoice.id!}`);
  };

  const onUpdateClick = (invoice: ReceivedInvoice) => {
    const baseUrl = getRoutePath("addReceivedInvoice");
    history.push(`${baseUrl}?id=${invoice.id!}`);
  };

  const onDeleteClick = (invoice: ReceivedInvoice) => {
    setDeletingInvoice(invoice);
    setShowInvoiceDeleteModal(true);
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("receivedInvoices")) {
        return;
      }
      fetchInvoices();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, taxPayer]);

  useEffect(() => {
    setLoading(true);
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer]);

  const onInvoiceDeleteCancel = () => {
    setShowInvoiceDeleteModal(false);
  };

  const onInvoiceDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowInvoiceDeleteModal(false);
    setDeletingInvoice(undefined);
    setLoading(true);
    fetchInvoices();
  };

  const onPDFClick = async (invoice: ReceivedInvoice) => {
    const pdfState = await getItem<{ url: string }>(
      `/received_invoices/${invoice.id}/pdf/`,
    );

    if (pdfState.status !== SUCCESS || pdfState.data === undefined) {
      let message = "Error al tratar de obtener el PDF";
      if (pdfState.detail !== undefined) {
        message = pdfState.detail;
      }
      errorAlert(message);
      return;
    }
    window.open(getObjectMinioUrl(pdfState.data.url), "_blank");
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onStartDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }

    const date = new Date((value as moment.Moment).toISOString());

    setStartDate(date);
  };

  const onEndDateChange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }

    const date = new Date((value as moment.Moment).toISOString());

    setEndDate(date);
  };

  const onSearch = useCallback(() => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);

    if (search !== undefined) {
      urlParams.set("search", search);
    } else {
      urlParams.delete("search");
    }

    if (startDate !== null) {
      urlParams.set("start_date", startDate.toISOString());
    } else {
      urlParams.delete("start_date");
    }

    if (endDate !== null) {
      urlParams.set("end_date", endDate.toISOString());
    } else {
      urlParams.delete("end_date");
    }

    let url = `?${urlParams.toString()}`;
    history.push(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, startDate, endDate]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  const onDownloadExcelClick = useCallback(async () => {
    let additionalParams = new Map<string, string>();

    additionalParams.set("tax_payer_id", taxPayer.id!.toString());

    if (search !== undefined && search !== "") {
      additionalParams.set("search", search);
    }

    if (startDate !== null) {
      additionalParams.set("start_date", startDate.toISOString());
    }
    if (endDate !== null) {
      additionalParams.set("end_date", endDate.toISOString());
    }

    const excelState = await getItem<{ url: string }>(
      "/received_invoices/excel/",
      additionalParams,
    );

    if (excelState.status !== SUCCESS || excelState.data === undefined) {
      let message = "Error al tratar de obtener el Excel";
      if (excelState.detail !== undefined) {
        message = excelState.detail;
      }
      errorAlert(message);
      return;
    }
    window.open(getObjectMinioUrl(excelState.data.url), "_blank");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer, search, startDate, endDate]);

  const isMobile = useIsMobile();

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Facturas Recibidas(Compras)</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent permissionName={INVOICES_CREATE}>
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <SystemNotificationBanners />
            <CCardBody>
              <CRow>
                <CCol>
                  <CButton onClick={toggleFilterCollapse} className={"pl-0 filter-toggle"}>
                    <h5>
                      <i className="fa fa-filter mr-2"></i>
                      {filterCollapse ? "Ocultar Filtros" : "Ver Filtros"}
                    </h5>
                  </CButton>
                </CCol>
              </CRow>
              <CCollapse show={filterCollapse}>
                <CRow className={"mt-2"}>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Desde:</CLabel>
                  </CCol>
                  <CCol>
                    <Datetime
                      className="pl-0"
                      onChange={onStartDateChange}
                      value={startDate !== null ? startDate : ""}
                      locale="es/PY"
                      dateFormat="DD/MM/YYYY"
                      timeFormat="HH:mm"
                      utc={true}
                      closeOnSelect={true}
                    ></Datetime>
                  </CCol>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Hasta:</CLabel>
                  </CCol>
                  <CCol>
                    <Datetime
                      className="pl-0"
                      onChange={onEndDateChange}
                      value={endDate !== null ? endDate : ""}
                      locale="es/PY"
                      dateFormat="DD/MM/YYYY"
                      timeFormat="HH:mm"
                      utc={true}
                      closeOnSelect={true}
                    ></Datetime>
                  </CCol>
                </CRow>

                <CRow className="mb-4">
                  <CCol>
                    <CLabel
                      className="visually-hidden"
                      htmlFor="autoSizingInputGroup"
                    ></CLabel>
                    <CInputGroup>
                      <CInputGroupText>
                        <i className="fa fa-search"></i>
                      </CInputGroupText>
                      <CInput
                        type="text"
                        placeholder="Introduzca el termino de búsqueda"
                        onChange={onSearchChange}
                        value={emptyValueOnUndefined(search)}
                        onKeyDown={handleKeyDown}
                      />
                      <CButton
                        type="button"
                        color="primary"
                        variant="outline"
                        onClick={onSearch}
                      >
                        Filtrar
                      </CButton>
                    </CInputGroup>
                  </CCol>
                </CRow>
              </CCollapse>
              <CRow className="mb-2">
                <CCol>
                  <CButtonGroup>
                    <CButton
                      color="success"
                      className="fa fa-file-excel"
                      title="Descargar Excel"
                      aria-label="Descargar Excel"
                      onClick={onDownloadExcelClick}
                    ></CButton>
                  </CButtonGroup>
                </CCol>
              </CRow>

              <div>
                {isMobile ? (
                  // Mobile view: collapsible cards
                  <>
                    {invoices.length === 0 ? (
                      <h2 className="text-center">
                        No se encontraron registros
                      </h2>
                    ) : (
                      invoices.map((invoice) => (
                        <ReceivedInvoiceCard
                          key={invoice.id}
                          invoice={invoice}
                          onPDFClick={onPDFClick}
                          onReadClick={onReadClick}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                        />
                      ))
                    )}
                  </>
                ) : (
                  <CDataTable
                    noItemsView={{
                      noItems: "No se encontraron registros",
                      noResults: "No se encontraron registros",
                    }}
                    addTableClasses={"table-fixed"}
                    fields={fields}
                    items={invoices}
                    striped
                    border
                    loading={loading}
                    responsive
                    scopedSlots={{
                      invoiceDate: (item: ReceivedInvoice) => {
                        return (
                          <td className="text-center">
                            {formatUTCDateTime(new Date(item.invoiceDate!))}
                          </td>
                        );
                      },
                      invoiceNumber: (item: ReceivedInvoice) => {
                        if (item.invoiceNumber === undefined) {
                          return <td className="text-center">-</td>;
                        }
                        return (
                          <td className="text-center">
                            {formatReceivedInvoiceNumber(item)}
                          </td>
                        );
                      },
                      supplierRuc: (item: ReceivedInvoice) => (
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle" }}
                        >
                          {item.supplierRuc || "-"}
                        </td>
                      ),
                      supplierSocialName: (item: ReceivedInvoice) => (
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle" }}
                        >
                          {item.supplierSocialName || "-"}
                        </td>
                      ),
                      amount: (item: ReceivedInvoice) => (
                        <td
                          className="text-center"
                          style={{ verticalAlign: "middle" }}
                        >
                          {formatToCurrency(item.amount!, item.currency!)}
                        </td>
                      ),
                      files: (item: ReceivedInvoice) => {
                        let pdfButton = null;

                        if (item.pdfPath !== undefined) {
                          pdfButton = (
                            <PermissionRequiredComponent
                              permissionName={INVOICES_RETRIEVE}
                            >
                              <CTooltip content="PDF">
                                <CButton
                                  className="text-white"
                                  color="danger"
                                  onClick={() => {
                                    onPDFClick(item);
                                  }}
                                >
                                  <i className="fa fa-file-pdf"></i>
                                </CButton>
                              </CTooltip>
                            </PermissionRequiredComponent>
                          );
                        }

                        return (
                          <td className="text-center">
                            <CButtonGroup>{pdfButton}</CButtonGroup>
                          </td>
                        );
                      },
                      actions: (item: ReceivedInvoice) => (
                        <ReceivedInvoiceCrudActions
                          invoice={item}
                          onReadClick={onReadClick}
                          onUpdateClick={onUpdateClick}
                          onDeleteClick={onDeleteClick}
                        />
                      ),
                    }}
                  />
                )}
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <ReceivedInvoiceDeleteModal
        show={showInvoiceDeleteModal}
        onCancel={onInvoiceDeleteCancel}
        onSuccess={onInvoiceDeleteSuccess}
        invoice={deletingInvoice}
      />
    </>
  );
};

export default ReceivedInvoiceCrud;
